/**
 * royaleColors.ts
 *
 * Per-player accent colour.
 *
 * Two modes, switched by the `DIFFERENT_COLOR` config in royaleConstants:
 *   - DIFFERENT_COLOR = true (default): each player gets a DISTINCT colour
 *     assigned by lobby JOIN ORDER, and the local user ("you") is ALWAYS green
 *     (SELF_COLOR) so a player consistently sees themselves in green. The other
 *     players draw from PLAYER_COLOR_SEQUENCE (green excluded) in join order;
 *     beyond the palette length they cycle.
 *   - DIFFERENT_COLOR = false: legacy stable per-userId hash (colorForUser).
 *
 * Because join-order + "who is you" are needed, the live colour is resolved via
 * `buildColorMap(orderedUserIds, myUserId)` → a `(userId) => colour` function.
 * Components build the map once from the ordered roster and call it per row.
 */

import {
  DIFFERENT_COLOR,
  PLAYER_COLOR_SEQUENCE,
  SELF_COLOR,
} from '../royaleConstants';

// Legacy palette (per-userId hash). Kept for the DIFFERENT_COLOR=false path.
const PLAYER_COLORS = [
  '#1A7E73',
  '#9A7600',
  '#C05828',
  '#6845B0',
  '#2878C0',
  '#B03888',
  '#1A7535',
  '#985018',
  '#A81818',
  '#2858A8',
];

/** Stable colour for a userId (simple string hash → palette index). Legacy. */
export function colorForUser(userId: string): string {
  let h = 0;
  for (let i = 0; i < userId.length; i++) {
    // eslint-disable-next-line no-bitwise
    h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return PLAYER_COLORS[h % PLAYER_COLORS.length];
}

/** A resolved colour lookup for one race/roster. */
export type ColorMap = (userId: string) => string;

/**
 * Build the colour lookup for a race.
 *
 * @param orderedUserIds player ids in JOIN ORDER (lobby roster / session
 *   playerIds). Only the order matters; ids not in the list still resolve
 *   (fall back to the hash) so a late/edge player never renders colourless.
 * @param myUserId       the local user — forced to SELF_COLOR when
 *   DIFFERENT_COLOR is on.
 */
export function buildColorMap(
  orderedUserIds: string[],
  myUserId: string,
): ColorMap {
  if (!DIFFERENT_COLOR) {
    return colorForUser;
  }

  const map: Record<string, string> = {};
  let seqIdx = 0;
  for (const id of orderedUserIds) {
    const key = String(id);
    if (key in map) {
      continue;
    }
    if (key === String(myUserId)) {
      map[key] = SELF_COLOR;
      continue;
    }
    // Next distinct sequence colour (green is excluded from the sequence, so
    // it stays unique to "you"); cycle if there are more players than colours.
    map[key] = PLAYER_COLOR_SEQUENCE[seqIdx % PLAYER_COLOR_SEQUENCE.length];
    seqIdx += 1;
  }

  return (userId: string) => {
    const key = String(userId);
    if (key === String(myUserId)) {
      return SELF_COLOR;
    }
    if (map[key]) {
      return map[key];
    }
    // Unknown id (not in the ordered roster yet): stable hash so it still has a
    // colour without disturbing the join-order assignment.
    return colorForUser(key);
  };
}
